import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import {
  GET_ONBOARDING_PROGRESS,
  SUBMIT_AND_GEN_AGENT,
} from '~/services/onboarding';
import { Business, IResponseOnboardingProgress } from './types';
import { CreateBusiness } from './components/CreateBusiness';
import { TypingEffect } from '~/components/shared/TypingEffect';
import { motion } from 'motion/react';
import { Background } from './components/Background';
import { PresentationSlide } from './components/PresentationSlide';
import { Footer } from './components/Footer';
import { WhatsappTestNumber } from './components/WhatsappTestNumber';
import { UpdateBusinessRules } from './components/UpdateBusinessRules';
import { UpdateBusinessBrand } from './components/UpdateBusinessBrand';

import { ReactComponent as ImageOne } from '~/assets/svg/step_0.svg';
import { ReactComponent as ImageTwo } from '~/assets/svg/step_1.svg';
import { ReactComponent as ImageThree } from '~/assets/svg/step_2.svg';
import { ReactComponent as ImageFour } from '~/assets/svg/step_3.svg';
import { ReactComponent as ImageFive } from '~/assets/svg/step_4.svg';
import { ReactComponent as ImageShopify } from '~/assets/svg/step_shopify.svg';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useSession } from '~/components/contexts/SessionProvider';
import { XCircleIcon } from '@heroicons/react/24/solid';
import { ConnectShopify } from './components/ConnectShopify';
import { Logout } from '~/components/shared/Logout';
import { FinalOnboarding } from './components/FinalOnboarding';
import React from 'react';
import { set } from 'lodash';

// const Images = [ImageOne, ImageTwo, ImageThree, ImageFour, ImageFive];

const OnboardingScreen = () => {
  const [t] = useTranslation('onboarding');
  const [StepComponents, setStepComponents] = useState([
    PresentationSlide,
    CreateBusiness,
    WhatsappTestNumber,
    UpdateBusinessRules,
    UpdateBusinessBrand,
  ]);
  const [Images, setImages] = useState([
    ImageOne,
    ImageTwo,
    ImageThree,
    ImageFour,
    ImageFive,
  ]);

  const { getOwner, owner } = useSession();
  const { data, loading } = useQuery<IResponseOnboardingProgress>(
    GET_ONBOARDING_PROGRESS,
  );
  const [business, setBusiness] = useState<Partial<Business>>({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [generatedAgentId, setGeneratedAgentId] = useState<string | undefined>(undefined);
  const [submit, { loading: loadingSubmit, data: agentData }] = useMutation(
    SUBMIT_AND_GEN_AGENT,
    {
      onCompleted: async (data) => {
        if (data.response.hasError) {
          console.error(data.response.message);
          setSubmitForm(false);
        } else {
          await getOwner();
          console.log('business to submit>>>:', data.response.data._id);

          // Guardar el agent_id generado
          setGeneratedAgentId(data.response.data._id || data.response.data?.agent_id);
          setShowSuccessModal(true);
        }
      },
    },
  );
  const [step, setStep] = useState(0);
  const [disable, setDisable] = useState(false);
  /**
   * This state is used to trigger the form submission
   * logic for mutation should be contained in the respective step component
   * and the form submission should be triggered by the parent component
   * error handling should also be done in the respective step component
   */
  const [submitForm, setSubmitForm] = useState(false);



  const calculateNextStep = (
    data: IResponseOnboardingProgress['response']['data'],
  ) => {
    let step = 0;
    if (!data.business) return setStep(step);
    setBusiness(data.business);
    step++;

    if (data.business?.agent_type === 'ECOMMERCE') {
      setStepComponents([
        PresentationSlide,
        CreateBusiness,
        WhatsappTestNumber,
        ConnectShopify,
        UpdateBusinessRules,
        UpdateBusinessBrand,
      ]);
      setImages([
        ImageOne,
        ImageTwo,
        ImageThree,
        ImageShopify,
        ImageFour,
        ImageFive,
      ]);
      if (data.business?.skip_shopify) step++;
      if (data.business?.shopify?.shop_name) step++;
    }
    if (data.business?._id) step++;
    if (data.business?.whatsapp?.channel_id) step++;
    if (data.business?.business_rules) step++;
    setStep(step);
  };

  useEffect(() => {
    if (!loading) {
      calculateNextStep(data?.response?.data || {});
    }
  }, [data]);

  const moveNextStep = (business?: Business) => {
    const nextStep = step + 1;
    setStep(nextStep);
    setSubmitForm(false);
    if (business) setBusiness(business);
 
    if (nextStep >= StepComponents.length) {
      if (business && business._id) {
        submit({
          variables: {
            input: {
              business_id: business._id,
            },
          },
        });
      }
    }
  };

  const moveBackStep = () => {
    setStep((prev) => prev - 1);
    setSubmitForm(false);
    setDisable(false);
  };

  useEffect(() => {
    // If we've reached the last step and we're submitting
    if (step >= StepComponents.length && submitForm) {
      submit({
        variables: {
          input: {
            business_id: business._id,
          },
        },
      });
    }
  }, [step, submitForm]);

  useEffect(() => {
     setGeneratedAgentId(agentData?.response?.data?._id);
  }, [agentData]);

  // Reset disable state when on the last step to ensure submit button is clickable
  useEffect(() => {
    if (step === StepComponents.length - 1) {
      setDisable(false);
    }
  }, [step, StepComponents.length]);

  // Determine which step component to render
  let Step = StepComponents[0];
  if (step < StepComponents.length) {
    Step = StepComponents[step];
  }

  
  return (
    <div className="fixed inset-0 z-10 flex h-full h-screen bg-base-100">
      {loading || loadingSubmit ? (
        <motion.div
          className="m-auto flex h-full w-full flex-col items-center justify-center gap-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div data-testid="loading" className="loader" />
          <TypingEffect />
          {loadingSubmit && <p className="text-center text-sm">Generando tu agente, esto puede tardar unos segundos...</p>}
        </motion.div>
      ) : (
        <motion.div
          data-testid="onboarding"
          className="w-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {showSuccessModal ? (
            <FinalOnboarding 
              agentId={generatedAgentId}
              business={business}
            />
          ) : (
            <div className="flex flex-row items-center justify-center">
              <div className="w-full overflow-hidden px-3 md:w-3/5 md:px-12 flex flex-col items-center justify-center">
                <div className="mt-4 h-8 self-start">
                  {owner.agents.length > 0 ? (
                    <Link to="/">
                      <XCircleIcon className="h-6 w-6 cursor-pointer opacity-40" />
                    </Link>
                  ) : (
                    <Logout />
                  )}
                </div>
                <div className="max-w-full md:max-w-[700px]">
                  <div className="mt-4 min-h-[50vh] max-h-[75vh] overflow-x-hidden overflow-y-auto touch-auto md:mt-16 md:min-h-[60vh]">
                    {Step && (
                      <Step
                        business={business}
                        moveNextStep={moveNextStep}
                        submitForm={submitForm}
                        setDisable={setDisable}
                        setSubmitForm={setSubmitForm}
                      />
                    )}
                     <Footer
                    disable={disable}
                    moveBackStep={moveBackStep}
                    submitForm={submitForm}
                    steps={StepComponents.length}
                    currentStep={step + 1}
                    onSubmit={() => {
                      setSubmitForm(true);
                    }}
                  />
                  </div>
                </div>
              </div>
              <Background>
                {step < Images.length && React.createElement(Images[step], {
                  className: 'w-full h-full object-contain',
                  title: 'settings',
                })}
              </Background>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default OnboardingScreen;
